/**
 * CharterDeliveryModal — the small modal that records a Charter as
 * posted. Extracted from DeliveryTab.tsx on 2026-05-29 so both the
 * Charters → Delivery list AND the Portfolio board's "Deliver Charter"
 * cards use the same flow (one modal, one save path, one feedback
 * channel). Mirrors the spec in SOP-POS-ONBOARDING.md Section 2.
 *
 * Expanded 2026-06-01: alongside the delivery date + courier note it now
 * captures who the framed Charter is going to (To + delivery address,
 * defaulting from the property's own address + primary contact) and who
 * dispatched it (Sent by — only known at dispatch, covers the ambassador
 * hand-delivers case). To + address are the destination and survive a
 * "mark not delivered"; the dispatch artefacts are cleared. The waybill /
 * delivery-note upload is deferred to a follow-up (the pos-documents
 * bucket needs portal-user storage rules first).
 *
 * Pure UI + DB write. Caller supplies the property and is notified
 * when something saved so the surrounding list can refresh.
 */

import { useEffect, useState } from 'react';
import { formatDateLong } from '../../../../utils/formatters';
import type { ChangeEvent, FormEvent } from 'react';
import type { SupabaseClient } from '@supabase/supabase-js';
import { useToast } from '../../../../components/Toast';

export interface CharterDeliveryTarget {
  id: string;
  property_name: string;
  charter_number: string | null;
  charter_issued_at: string | null;
  charter_delivered_at: string | null;
  charter_delivery_notes: string | null;
  charter_delivery_to: string | null;
  charter_delivery_address: string | null;
  charter_delivery_sent_by: string | null;
  // Storage PATH (not a public URL) in the private pos-documents bucket.
  // Signed on view, same as bill-payment-proofs.
  charter_delivery_doc_url: string | null;
  // Defaults source — the property's own address + primary contact.
  // Used to pre-fill To + delivery address when nothing's captured yet.
  address_line1: string | null;
  address_line2: string | null;
  city: string | null;
  province: string | null;
  postal_code: string | null;
  contact_first_name: string | null;
  contact_last_name: string | null;
}

interface Props {
  supabase: SupabaseClient;
  property: CharterDeliveryTarget;
  canMark: boolean;
  onClose: () => void;
  /** Called after a successful save or clear so the caller can refresh. */
  onSaved?: () => void | Promise<void>;
}

function formatLongDate(s: string | null | undefined): string {
  if (!s) return '—';
  return formatDateLong(s);
}

function todayIso(): string {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Default recipient name from the property's primary contact. */
function defaultTo(p: CharterDeliveryTarget): string {
  return `${p.contact_first_name || ''} ${p.contact_last_name || ''}`.trim();
}

/** Default multi-line delivery address from the property's own address. */
function defaultAddress(p: CharterDeliveryTarget): string {
  const cityLine = [p.city, p.province].filter(Boolean).join(', ');
  return [p.address_line1, p.address_line2, cityLine, p.postal_code]
    .map((s) => (s || '').trim())
    .filter(Boolean)
    .join('\n');
}

export default function CharterDeliveryModal({ supabase, property, canMark, onClose, onSaved }: Props) {
  const { showToast } = useToast();
  const [editDate, setEditDate] = useState('');
  const [editTo, setEditTo] = useState('');
  const [editAddress, setEditAddress] = useState('');
  const [editSentBy, setEditSentBy] = useState('');
  const [editNotes, setEditNotes] = useState('');
  const [docPath, setDocPath] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);

  useEffect(() => {
    setEditDate(
      property.charter_delivered_at
        ? property.charter_delivered_at.slice(0, 10)
        : todayIso(),
    );
    setEditTo(property.charter_delivery_to ?? defaultTo(property));
    setEditAddress(property.charter_delivery_address ?? defaultAddress(property));
    setEditSentBy(property.charter_delivery_sent_by ?? '');
    setEditNotes(property.charter_delivery_notes || '');
    setDocPath(property.charter_delivery_doc_url ?? null);
    setSaveError(null);
  }, [property]);

  async function handleWaybillUpload(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = '';
    if (!file || !canMark) return;
    setUploading(true);
    setSaveError(null);
    try {
      const ext = (file.name.split('.').pop() || 'pdf').toLowerCase();
      const path = `waybills/${property.id}/waybill-${Date.now()}.${ext}`;
      const { error } = await supabase.storage
        .from('pos-documents')
        .upload(path, file, { cacheControl: '3600', upsert: true, contentType: file.type });
      if (error) throw error;
      setDocPath(path);
      showToast('Waybill uploaded. Save to attach it to the delivery.', 'success');
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : 'Upload failed.');
    } finally {
      setUploading(false);
    }
  }

  async function viewWaybill() {
    if (!docPath) return;
    const { data } = await supabase.storage.from('pos-documents').createSignedUrl(docPath, 300);
    if (data?.signedUrl) window.open(data.signedUrl, '_blank', 'noopener');
  }

  // Marking (or clearing) delivery changes what the customer sees on their
  // Charter Suite, which is the statically built public site. Ping the POS
  // site rebuild so the change goes live without anyone hitting "Update
  // Directory" by hand. Fire-and-forget — a failed rebuild must never block
  // or fail the delivery save itself.
  async function triggerCustomerSiteRebuild() {
    try {
      const { data: { session } } = await supabase.auth.getSession();
      await fetch(
        `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/pos-website-rebuild`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${session?.access_token}`,
          },
          body: JSON.stringify({}),
        },
      );
    } catch (err) {
      console.warn('[CharterDeliveryModal] customer site rebuild trigger failed:', err);
    }
  }

  async function handleSave(e: FormEvent) {
    e.preventDefault();
    if (!canMark) return;
    if (!editDate) {
      setSaveError('Date posted is required.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      const isoStamp = `${editDate}T00:00:00.000Z`;
      const { error } = await supabase
        .from('hosp_properties')
        .update({
          charter_delivered_at: isoStamp,
          charter_delivery_notes: editNotes.trim() || null,
          charter_delivery_to: editTo.trim() || null,
          charter_delivery_address: editAddress.trim() || null,
          charter_delivery_sent_by: editSentBy.trim() || null,
          charter_delivery_doc_url: docPath,
        })
        .eq('id', property.id);
      if (error) throw error;
      void triggerCustomerSiteRebuild();
      showToast(`Charter for ${property.property_name} marked as posted. Customer Charter Suite updating.`, 'success');
      onClose();
      await onSaved?.();
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  async function handleClearDelivery() {
    if (!canMark) return;
    if (!confirm(`Mark Charter for ${property.property_name} as not yet delivered?`)) return;
    setSaving(true);
    setSaveError(null);
    try {
      // Keep the destination (To + address) — that's captured at the
      // certificate step. Only the dispatch artefacts are cleared.
      const { error } = await supabase
        .from('hosp_properties')
        .update({
          charter_delivered_at: null,
          charter_delivery_notes: null,
          charter_delivery_sent_by: null,
          charter_delivery_doc_url: null,
        })
        .eq('id', property.id);
      if (error) throw error;
      setDocPath(null);
      void triggerCustomerSiteRebuild();
      showToast(`Charter for ${property.property_name} set back to pending. Customer Charter Suite updating.`, 'info');
      onClose();
      await onSaved?.();
    } catch (err: unknown) {
      setSaveError(err instanceof Error ? err.message : 'Failed to save.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal modal-md" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h2 className="modal-title">Charter Delivery</h2>
          <button type="button" className="modal-close" onClick={onClose}>&times;</button>
        </div>
        <form onSubmit={handleSave}>
          <div className="modal-body">
            <div className="mb-md">
              <div className="fw-600 fs-base">{property.property_name}</div>
              <div className="text-muted fs-sm">
                <span className="ref-code">{property.charter_number}</span>
                {property.charter_issued_at ? ` · Earned ${formatLongDate(property.charter_issued_at)}` : ''}
              </div>
            </div>

            {saveError && (
              <div className="alert alert-error mb-md">{saveError}</div>
            )}

            <div className="form-group mb-md">
              <label className="form-label">To (attention of)</label>
              <input
                type="text"
                className="form-input"
                value={editTo}
                onChange={(e) => setEditTo(e.target.value)}
                placeholder="Recipient name"
              />
            </div>

            <div className="form-group mb-md">
              <label className="form-label">Delivery address</label>
              <textarea
                className="form-input form-textarea"
                value={editAddress}
                onChange={(e) => setEditAddress(e.target.value)}
                rows={3}
                placeholder="Where the framed Charter is sent"
              />
            </div>

            <div className="form-group mb-md">
              <label className="form-label">Date posted *</label>
              <input
                type="date"
                className="form-input"
                value={editDate}
                onChange={(e) => setEditDate(e.target.value)}
                max={todayIso()}
                required
              />
            </div>

            <div className="form-group mb-md">
              <label className="form-label">Sent by</label>
              <input
                type="text"
                className="form-input"
                value={editSentBy}
                onChange={(e) => setEditSentBy(e.target.value)}
                placeholder="Who dispatched it (courier, ambassador, etc.)"
              />
            </div>

            <div className="form-group mb-md">
              <label className="form-label">Delivery notes (optional)</label>
              <input
                type="text"
                className="form-input"
                value={editNotes}
                onChange={(e) => setEditNotes(e.target.value)}
                placeholder="Courier reference, contact, etc."
              />
            </div>

            <div className="form-group mb-md">
              <label className="form-label">Waybill / proof of delivery (optional)</label>
              {docPath && (
                <div className="mb-sm">
                  <button type="button" className="btn btn-secondary" onClick={() => void viewWaybill()}>
                    View current file
                  </button>
                </div>
              )}
              <input
                type="file"
                className="form-input"
                accept="image/jpeg,image/png,image/webp,application/pdf"
                onChange={(e) => void handleWaybillUpload(e)}
                disabled={uploading || !canMark}
              />
              {uploading && <div className="text-muted fs-xs mt-xs">Uploading…</div>}
            </div>
          </div>
          <div className="modal-footer">
            {property.charter_delivered_at ? (
              <button
                type="button"
                className="btn btn-ghost"
                onClick={handleClearDelivery}
                disabled={saving}
                style={{ marginRight: 'auto' }}
                title="Mark this Charter as not yet delivered"
              >
                Mark not delivered
              </button>
            ) : null}
            <button type="button" className="btn" onClick={onClose} disabled={saving}>
              Cancel
            </button>
            <button type="submit" className="btn btn-primary btn-pos-gold" disabled={saving}>
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
